<script setup>
import { computed, reactive, watch } from "vue";
import * as bootstrap from "bootstrap";
import { usePlanStore } from "@/stores/planStore";

const props = defineProps({
  plan: {
    type: Object,
    default: null,
  },
  deletePlan: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["plan-updated", "modal-closed"]);

const planStore = usePlanStore();

const MODULE_GROUPS = [
  {
    key: "hrm",
    label: "HRM & Payroll",
    children: [
      { key: "hrm_employee", label: "Employee Management" },
      { key: "hrm_payroll", label: "Payroll Module" },
      { key: "hrm_attendance", label: "Attendance Module" },
      { key: "hrm_holidays", label: "Leave Module" },
      { key: "hrm_tickets", label: "Tickets / Support" },
    ],
  },
  {
    key: "loans_savings",
    label: "Loans & Savings",
    children: [
      { key: "loans", label: "Loans" },
      { key: "savings", label: "Savings" },
    ],
  },
  {
    key: "administration",
    label: "Administration & Reporting",
    children: [
      { key: "admin_reports", label: "Reports" },
      { key: "admin_settings", label: "Settings" },
      { key: "admin_assets", label: "Assets" },
      { key: "admin_help_support", label: "Help & Supports" },
    ],
  },
  {
    key: "finance_accounts",
    label: "Finance & Accounts",
    children: [
      { key: "fa_sales", label: "Sales" },
      { key: "fa_accounting", label: "Accounting" },
    ],
  },
  {
    key: "projects",
    label: "Project Management",
    children: [
      { key: "projects", label: "Projects" },
      { key: "tasks", label: "Tasks" },
      { key: "task_board", label: "Task Board" },
    ],
  },
  {
    key: "crm",
    label: "CRM",
    children: [
      { key: "crm_contacts", label: "Contacts" },
      { key: "crm_clients", label: "Clients" },
      { key: "crm_deals", label: "Deals" },
      { key: "crm_leads", label: "Leads" },
      { key: "crm_pipeline", label: "Pipeline" },
      { key: "crm_analytics", label: "Analytics" },
      { key: "crm_activities", label: "Activities" },
    ],
  },
];

const allModuleKeys = MODULE_GROUPS.flatMap((group) =>
  group.children.map((child) => child.key),
);

const expandedGroups = reactive(
  Object.fromEntries(MODULE_GROUPS.map((group) => [group.key, false])),
);

const form = reactive({
  name: "",
  currency: "KES",
  position: 1,
  price_type: "fixed",
  price: 0,
  license_fee: 0,
  is_contact_sales: false,
  allow_trial: true,
  trial_interval_value: 14,
  trial_interval_unit: "days",
  support_period_months: 3,
  grace_period_months: 2,
  is_recommended: false,
  full_access: false,
  status: "active",
  description: "",
  modules: [],
  marketing_features_text: "",
});

const isEdit = computed(() => !!props.plan?.id);

const formatMoney = (amount) =>
  `${String(form.currency || "KES").toUpperCase()} ${Number(amount || 0).toFixed(2)}`;

const marketingFeatureLines = computed(() =>
  String(form.marketing_features_text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean),
);

const publicPreview = computed(() => ({
  planPrice: form.is_contact_sales ? "Contact Sales" : formatMoney(form.price),
  licenseFee: formatMoney(form.license_fee),
  trial: form.allow_trial && Number(form.trial_interval_value || 0) > 0
    ? `${form.trial_interval_value} ${Number(form.trial_interval_value) === 1 ? form.trial_interval_unit.replace(/s$/, "") : form.trial_interval_unit}`
    : "No trial",
  support: Number(form.support_period_months || 0) > 0
    ? `${form.support_period_months} month${Number(form.support_period_months) === 1 ? "" : "s"} free`
    : "Not configured",
}));

const isAllSelected = computed(
  () =>
    form.modules.length > 0 &&
    allModuleKeys.every((key) => form.modules.includes(key)),
);

const groupIsFullySelected = (group) =>
  group.children.every((child) => form.modules.includes(child.key));

const groupHasAnySelected = (group) =>
  group.children.some((child) => form.modules.includes(child.key));

const toggleSelectAllModules = () => {
  form.modules = isAllSelected.value ? [] : [...allModuleKeys];
};

const toggleGroupSelection = (group) => {
  if (groupIsFullySelected(group)) {
    form.modules = form.modules.filter(
      (item) => !group.children.some((child) => child.key === item),
    );
    return;
  }

  form.modules = Array.from(
    new Set([...form.modules, ...group.children.map((child) => child.key)]),
  );
};

const resetForm = () => {
  form.name = "";
  form.currency = "KES";
  form.position = 1;
  form.price_type = "fixed";
  form.price = 0;
  form.license_fee = 0;
  form.is_contact_sales = false;
  form.allow_trial = true;
  form.trial_interval_value = 14;
  form.trial_interval_unit = "days";
  form.support_period_months = 3;
  form.grace_period_months = 2;
  form.is_recommended = false;
  form.full_access = false;
  form.status = "active";
  form.description = "";
  form.modules = [];
  form.marketing_features_text = "";
};

const fillFormFromPlan = (plan) => {
  if (!plan) {
    resetForm();
    return;
  }

  form.name = plan.name || "";
  form.currency = plan.currency || "KES";
  form.position = Number(plan.position || 1);
  form.price_type = plan.price_type || "fixed";
  form.price = Number(plan.price ?? 0);
  form.license_fee = Number(plan.license_fee ?? 0);
  form.is_contact_sales = !!plan.is_contact_sales;
  form.allow_trial = !!plan.allow_trial;
  form.trial_interval_value = Number(
    plan.trial_interval_value ?? plan.trial_days ?? 0,
  );
  form.trial_interval_unit = plan.trial_interval_unit || "days";
  form.support_period_months = Number(plan.support_period_months ?? 0);
  form.grace_period_months = Number(plan.grace_period_months ?? 0);
  form.is_recommended = !!plan.is_recommended;
  form.full_access = !!plan.full_access;
  form.status = plan.status || "active";
  form.description = plan.description || "";
  form.modules = Array.isArray(plan.modules) ? [...plan.modules] : [];
  form.marketing_features_text = Array.isArray(plan.marketing_features)
    ? plan.marketing_features.join("\n")
    : "";
};

watch(
  () => props.plan,
  (value) => {
    if (value?.id) {
      fillFormFromPlan(value);
    } else {
      resetForm();
    }
  },
  { immediate: true },
);

const hideModal = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  bootstrap.Modal.getOrCreateInstance(el).hide();
};

const payload = () => ({
  name: form.name,
  billing_period: "annual_license",
  currency: form.currency,
  position: form.position,
  price_type: form.price_type,
  price: Number(form.price || 0),
  license_fee: Number(form.license_fee || 0),
  is_contact_sales: !!form.is_contact_sales,
  allow_trial: !!form.allow_trial,
  trial_interval_value: form.allow_trial ? Number(form.trial_interval_value || 0) : null,
  trial_interval_unit: form.allow_trial ? form.trial_interval_unit : null,
  support_period_months: Number(form.support_period_months || 0) || null,
  grace_period_months: Number(form.grace_period_months || 0) || 0,
  is_recommended: !!form.is_recommended,
  full_access: !!form.full_access,
  status: form.status,
  description: form.description,
  modules: form.modules,
  marketing_features: marketingFeatureLines.value,
});

const handleSubmit = async (mode) => {
  try {
    if (mode === "create") {
      await planStore.createPlan(payload());
      hideModal("add_plans");
    } else if (props.plan?.id) {
      await planStore.updatePlan(props.plan.id, payload());
      hideModal("edit_plans");
    }

    emit("plan-updated");
  } catch {
    // store already handles toast
  }
};

const handleDelete = async () => {
  if (!props.deletePlan?.id) return;

  try {
    await planStore.deletePlan(props.deletePlan.id);
    hideModal("delete_modal");
    emit("plan-updated");
  } catch {
    // store already handles toast
  }
};

const closeCleanup = () => {
  emit("modal-closed");
};
</script>

<template>
  <div class="modal fade" id="add_plans" @hidden.bs.modal="closeCleanup">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Add New Plan</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="handleSubmit('create')">
          <div class="modal-body pb-0">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">Plan Name<span class="text-danger"> *</span></label>
                <input v-model="form.name" type="text" class="form-control" required />
              </div>

              <div class="col-md-2">
                <label class="form-label">Currency<span class="text-danger"> *</span></label>
                <select v-model="form.currency" class="form-select">
                  <option value="KES">KES</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>

              <div class="col-md-2">
                <label class="form-label">Position</label>
                <input v-model.number="form.position" type="number" min="1" class="form-control" />
              </div>

              <div class="col-md-2">
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-select">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div class="col-md-2">
                <label class="form-label">Price Type</label>
                <select v-model="form.price_type" class="form-select">
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>

              <div class="col-md-3">
                <label class="form-label">Plan Price</label>
                <input v-model.number="form.price" type="number" min="0" step="0.01" class="form-control" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Annual Licence Fee</label>
                <input v-model.number="form.license_fee" type="number" min="0" step="0.01" class="form-control" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Free Support (Months)</label>
                <input v-model.number="form.support_period_months" type="number" min="0" max="36" class="form-control" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Grace Period (Months)</label>
                <input v-model.number="form.grace_period_months" type="number" min="0" max="24" class="form-control" />
              </div>

              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.is_contact_sales" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Contact Sales Plan</label>
                </div>
              </div>

              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.allow_trial" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Allow Free Trial</label>
                </div>
              </div>

              <div class="col-md-3">
                <label class="form-label">Trial Length</label>
                <input v-model.number="form.trial_interval_value" :disabled="!form.allow_trial" type="number" min="0" class="form-control" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Trial Unit</label>
                <select v-model="form.trial_interval_unit" :disabled="!form.allow_trial" class="form-select">
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                </select>
              </div>

              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.is_recommended" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Recommended Plan</label>
                </div>
              </div>

              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.full_access" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Full System Access</label>
                </div>
              </div>

              <div class="col-12">
                <div class="alert alert-light border mb-0">
                  <div class="fw-semibold mb-2">Public Pricing Preview</div>
                  <div class="small text-muted mb-1">Plan price shown publicly: {{ publicPreview.planPrice }}</div>
                  <div class="small text-muted mb-1">Annual licence renewal amount: {{ publicPreview.licenseFee }}</div>
                  <div class="small text-muted mb-1">Trial shown publicly: {{ publicPreview.trial }}</div>
                  <div class="small text-muted">Support highlight: {{ publicPreview.support }}</div>
                </div>
              </div>

              <div class="col-md-12">
                <label class="form-label">Marketing Feature Bullets</label>
                <textarea
                  v-model="form.marketing_features_text"
                  class="form-control"
                  rows="4"
                  placeholder="One public-facing feature per line. Default modules should not be listed here."
                />
              </div>

              <div class="col-md-12">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-control" rows="3" />
              </div>

              <div class="col-12">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <h6 class="mb-0">Plan Modules</h6>
                  <label class="form-check-label mt-0 text-dark fw-medium">
                    <input class="form-check-input me-2" type="checkbox" :checked="isAllSelected" @change="toggleSelectAllModules" />
                    Select All
                  </label>
                </div>
              </div>

              <div class="col-12">
                <div v-for="group in MODULE_GROUPS" :key="group.key" class="border rounded p-2 mb-2">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="checkbox" :checked="groupIsFullySelected(group)" @change="toggleGroupSelection(group)" />
                      </div>
                      <h6 class="mb-0">
                        {{ group.label }}
                        <small v-if="groupHasAnySelected(group)" class="text-muted ms-1">
                          ({{ group.children.filter((child) => form.modules.includes(child.key)).length }} / {{ group.children.length }})
                        </small>
                      </h6>
                    </div>
                    <button type="button" class="btn btn-sm btn-light d-flex align-items-center" @click="expandedGroups[group.key] = !expandedGroups[group.key]">
                      <i class="ti" :class="expandedGroups[group.key] ? 'ti-chevron-up' : 'ti-chevron-down'"></i>
                    </button>
                  </div>
                  <div v-if="expandedGroups[group.key]" class="d-flex flex-wrap mt-2 ms-4">
                    <div v-for="child in group.children" :key="child.key" class="me-3 mb-2">
                      <label class="form-check-label mt-0 text-dark fw-medium">
                        <input v-model="form.modules" class="form-check-input me-2" type="checkbox" :value="child.key" />
                        {{ child.label }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span v-if="planStore.loading" class="spinner-border spinner-border-sm me-1"></span>
              Add Plan
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <div class="modal fade" id="edit_plans" @hidden.bs.modal="closeCleanup">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Edit Plan</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="handleSubmit('edit')">
          <div class="modal-body pb-0">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">Plan Name<span class="text-danger"> *</span></label>
                <input v-model="form.name" type="text" class="form-control" required />
              </div>
              <div class="col-md-2">
                <label class="form-label">Currency<span class="text-danger"> *</span></label>
                <select v-model="form.currency" class="form-select">
                  <option value="KES">KES</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label">Position</label>
                <input v-model.number="form.position" type="number" min="1" class="form-control" />
              </div>
              <div class="col-md-2">
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-select">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label">Price Type</label>
                <select v-model="form.price_type" class="form-select">
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Plan Price</label>
                <input v-model.number="form.price" type="number" min="0" step="0.01" class="form-control" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Annual Licence Fee</label>
                <input v-model.number="form.license_fee" type="number" min="0" step="0.01" class="form-control" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Free Support (Months)</label>
                <input v-model.number="form.support_period_months" type="number" min="0" max="36" class="form-control" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Grace Period (Months)</label>
                <input v-model.number="form.grace_period_months" type="number" min="0" max="24" class="form-control" />
              </div>
              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.is_contact_sales" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Contact Sales Plan</label>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.allow_trial" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Allow Free Trial</label>
                </div>
              </div>
              <div class="col-md-3">
                <label class="form-label">Trial Length</label>
                <input v-model.number="form.trial_interval_value" :disabled="!form.allow_trial" type="number" min="0" class="form-control" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Trial Unit</label>
                <select v-model="form.trial_interval_unit" :disabled="!form.allow_trial" class="form-select">
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                </select>
              </div>
              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.is_recommended" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Recommended Plan</label>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-check form-switch mt-4">
                  <input v-model="form.full_access" class="form-check-input" type="checkbox" role="switch" />
                  <label class="form-check-label">Full System Access</label>
                </div>
              </div>
              <div class="col-12">
                <div class="alert alert-light border mb-0">
                  <div class="fw-semibold mb-2">Public Pricing Preview</div>
                  <div class="small text-muted mb-1">Plan price shown publicly: {{ publicPreview.planPrice }}</div>
                  <div class="small text-muted mb-1">Annual licence renewal amount: {{ publicPreview.licenseFee }}</div>
                  <div class="small text-muted mb-1">Trial shown publicly: {{ publicPreview.trial }}</div>
                  <div class="small text-muted">Support highlight: {{ publicPreview.support }}</div>
                </div>
              </div>
              <div class="col-md-12">
                <label class="form-label">Marketing Feature Bullets</label>
                <textarea v-model="form.marketing_features_text" class="form-control" rows="4" placeholder="One public-facing feature per line. Default modules should not be listed here." />
              </div>
              <div class="col-md-12">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-control" rows="3" />
              </div>
              <div class="col-12">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <h6 class="mb-0">Plan Modules</h6>
                  <label class="form-check-label mt-0 text-dark fw-medium">
                    <input class="form-check-input me-2" type="checkbox" :checked="isAllSelected" @change="toggleSelectAllModules" />
                    Select All
                  </label>
                </div>
              </div>
              <div class="col-12">
                <div v-for="group in MODULE_GROUPS" :key="group.key" class="border rounded p-2 mb-2">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <div class="form-check me-3">
                        <input class="form-check-input" type="checkbox" :checked="groupIsFullySelected(group)" @change="toggleGroupSelection(group)" />
                      </div>
                      <h6 class="mb-0">
                        {{ group.label }}
                        <small v-if="groupHasAnySelected(group)" class="text-muted ms-1">
                          ({{ group.children.filter((child) => form.modules.includes(child.key)).length }} / {{ group.children.length }})
                        </small>
                      </h6>
                    </div>
                    <button type="button" class="btn btn-sm btn-light d-flex align-items-center" @click="expandedGroups[group.key] = !expandedGroups[group.key]">
                      <i class="ti" :class="expandedGroups[group.key] ? 'ti-chevron-up' : 'ti-chevron-down'"></i>
                    </button>
                  </div>
                  <div v-if="expandedGroups[group.key]" class="d-flex flex-wrap mt-2 ms-4">
                    <div v-for="child in group.children" :key="child.key" class="me-3 mb-2">
                      <label class="form-check-label mt-0 text-dark fw-medium">
                        <input v-model="form.modules" class="form-check-input me-2" type="checkbox" :value="child.key" />
                        {{ child.label }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span v-if="planStore.loading" class="spinner-border spinner-border-sm me-1"></span>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <div class="modal fade" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center py-4">
          <div class="mb-3">
            <i class="ti ti-trash text-danger" style="font-size: 42px"></i>
          </div>
          <h5 class="mb-2">Delete plan?</h5>
          <p class="text-muted mb-4">
            {{ props.deletePlan?.name || "This plan" }} will be removed permanently.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="handleDelete">
              <span v-if="planStore.loading" class="spinner-border spinner-border-sm me-1"></span>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
